import type { ReactElement, ReactNode } from "react";
import { Children, cloneElement, useCallback, useState } from "react";

import applyClassNames from "classnames";

import useDeactivateOnOutsideClick from "app/pages/sales-intelligence/hooks/useDeactivateOnOutsideClick";
import type { Translatable } from "app/pages/sales-intelligence/types";

import type { SelectionDropdownItem } from "../../models/selection-dropdown-item/types";

import SelectionDropdownButton from "./SelectionDropdownButton/SelectionDropdownButton";
import { StyledDDContainer, StyledContent, StyledItemsContainer, StyledFooterContainer } from "./styles";

const CONTAINER_CLASSNAME = "selection-dd-container";

type SelectionDropdownProps = {
    buttonText: Readonly<Translatable>;
    className?: string;
    isLoading?: boolean;
    isDisabled?: boolean;
    footer?: ReactNode;
    children: ReactElement[];
    onSelect: (item: SelectionDropdownItem) => void;
};

const SelectionDropdownView = ({
    buttonText,
    footer,
    children,
    className,
    isLoading = false,
    isDisabled = false,
    onSelect,
}: SelectionDropdownProps) => {
    const [isOpened, setIsOpened] = useState(false);

    const closeDropdown = useCallback(() => setIsOpened(false), []);

    const selectAndClose = useCallback(
        (item: SelectionDropdownItem) => {
            closeDropdown();
            onSelect(item);
        },
        [onSelect],
    );

    useDeactivateOnOutsideClick(CONTAINER_CLASSNAME, isOpened, closeDropdown);

    return (
        <StyledDDContainer
            className={applyClassNames(CONTAINER_CLASSNAME, className)}
            data-automation="table-actions-panel.selection-dropdown"
            data-automation-opened={isOpened}
        >
            <SelectionDropdownButton
                text={buttonText}
                isDisabled={isDisabled || isLoading}
                onClick={() => setIsOpened(true)}
            />
            <StyledContent open={isOpened}>
                <StyledItemsContainer>
                    {Children.map(children, (child) => {
                        return cloneElement(child, {
                            // @ts-expect-error error TS2698: Spread types may only be created from object types.
                            ...child.props,
                            onClick: selectAndClose,
                        });
                    })}
                </StyledItemsContainer>
                {typeof footer !== "undefined" ? <StyledFooterContainer>{footer}</StyledFooterContainer> : null}
            </StyledContent>
        </StyledDDContainer>
    );
};

export default SelectionDropdownView;
